<script setup>
import { computed, ref } from 'vue'
import { useFilingDetailStore } from '../../stores/filingDetailStore'
import { useRoute, useRouter } from 'vue-router'
import BModal from '../bootstrap/BModal.vue'
import CompanyApplicantCardItem from './CompanyApplicantCardItem.vue'
import StatusBadge from './StatusBadge.vue'

defineProps({
  companyApplicants: Array
})

const filingDetailStore = useFilingDetailStore()
const route = useRoute()
const router = useRouter()

const showDeleteModal = ref(false)
const companyApplicantToDelete = ref(null)

// Compute the path for adding a new companyApplicant
const addCompanyApplicantPath = computed(() => {
  return `/a/${route.params.teamSlug}/filings/${route.params.filingId}/add-company-applicant/`
})

// Function to handle button click
const navigateToAddCompanyApplicant = () => {
  router.push(addCompanyApplicantPath.value)
}

// Function to handle edit button click
const editCompanyApplicant = (companyApplicantId) => {
  router
    .push(
      `/a/${route.params.teamSlug}/filings/${route.params.filingId}/edit-company-applicant/${companyApplicantId}`
    )
    .catch((err) => {
      console.error('Navigation error:', err)
    })
}

const openDeleteModal = (companyApplicantId) => {
  companyApplicantToDelete.value = companyApplicantId
  showDeleteModal.value = true
}

const confirmDelete = () => {
  if (companyApplicantToDelete.value) {
    filingDetailStore.removeCompanyApplicantFromFiling(
      companyApplicantToDelete.value
    )
    showDeleteModal.value = false
    companyApplicantToDelete.value = null
  }
}
</script>

<template>
  <div class="card mb-3">
    <div class="card-header border-bottom pb-3">
      <h3 class="card-title">Company Applicant(s)</h3>
      <StatusBadge
        :isComplete="filingDetailStore.allCompanyApplicantsComplete"
        incompleteMsg="Incomplete"
        completeMsg="If All CompanyApplicants Present, Ready to Submit"
      />
    </div>
    <div v-editable class="alert alert-soft-secondary card-alert" role="alert">
      A company applicant is the person who files papers to create or register a
      company. They're important because they start the process of making a
      business official. To figure out who the company applicant is, look for:

      <ul class="text-start">
        <li>The person who sends in the forms to create a new company</li>
        <li>
          For companies that already exist, it's the person who registers the
          company to do business in the U.S.
        </li>
        <li>
          Sometimes, a lawyer or service does this. In that case, both the
          service and the person who told them to do it are company applicants
        </li>
      </ul>
      You can have up to two company applicants for each company. If your
      company was created before January 1, 2024, you don't need to report
      company applicants.
    </div>
    <div class="card-body">
      <!-- Cards for CompanyApplicants -->
      <div class="row gx-3 gy-2 align-items-center">
        <div v-if="companyApplicants.length >= 1">
          <CompanyApplicantCardItem
            v-for="companyApplicant in companyApplicants"
            :key="companyApplicant.id"
            :companyApplicant="companyApplicant"
            @edit="editCompanyApplicant"
            @delete="openDeleteModal"
          />
        </div>
        <div class="row justify-center" v-if="companyApplicants.length <= 0">
          <!-- Card -->
          <div class="card card-ghost card-centered h-100" href="#">
            <div class="card-body">
              <div class="mb-2 text-primary">
                <i class="fa-duotone fa-solid fa-user-plus fa-2xl"></i>
              </div>
              <span class="text-primary text-muted fs-3"
                >Add Your First Company Applicant</span
              >
            </div>
          </div>
          <!-- End Card -->
        </div>
        <button
          @click="navigateToAddCompanyApplicant"
          v-editable
          type="button"
          class="btn btn-outline-primary btn-lg"
        >
          <i class="fa-light fa-user-plus" aria-hidden="true"></i> Add
        </button>
      </div>
    </div>
  </div>
  <BModal
    id="deleteApplicantModal"
    title="Confirm Deletion"
    ok-title="Delete"
    :visible="showDeleteModal"
    @update:visible="showDeleteModal = $event"
    @ok="confirmDelete"
  >
    <p class="p-lead">
      Are you sure you want to delete this Company Applicant? This action cannot
      be undone.
    </p>
  </BModal>
</template>
